// Custom Block Library — gallery (Phase 2: curated built-ins + public USER blocks). A self-contained
// trigger button + modal: search/filter, inspect (description, params, example, version, safety tiers,
// risk notes, code preview, author/source, usage), report a user block, and "Add to strategy" (inserts a
// CUSTOM block with full provenance via patchBlocks). Gated by useBlockLibraryGate — when the library is
// killed, the button is hidden. Trust-state writes go through the block-publish edge fn (never client RLS).

import React, { useEffect, useMemo, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { Library, Search, X, Plus, ShieldCheck, Flag, BadgeCheck, User, GitFork } from 'lucide-react';
import { useStrategy } from '../../contexts/StrategyContext';
import { useBlockLibraryGate } from '../../config/useBlockLibraryGate';
import { buildEntryBlock } from '../../services/customBlockLibraryService';
import { loadLibrary, reportBlock } from '../../services/customBlockLibraryClient';
import { printBlock } from '../../engine/tslBlock/blockText';
import { TslBlockStudio } from './TslBlockStudio';
import type { BlockCategory, LibraryEntry } from '../../types/customBlockLibrary';

const CATEGORIES: Array<{ key: BlockCategory; label: string }> = [
  { key: 'indicators', label: 'Indicators' },
  { key: 'filters', label: 'Filters' },
  { key: 'sizing', label: 'Sizing' },
  { key: 'risk_rules', label: 'Risk rules' },
];

const TierBadge: React.FC<{ label: string; on: boolean }> = ({ label, on }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${on ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-400'}`}>
    {on && <ShieldCheck className="w-3 h-3" />}{label}
  </span>
);

export const CustomBlockLibraryButton: React.FC = () => {
  const gate = useBlockLibraryGate();
  const { blocks, patchBlocks } = useStrategy();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<BlockCategory | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [entries, setEntries] = useState<LibraryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [reported, setReported] = useState<Record<string, boolean>>({});
  const [forkOf, setForkOf] = useState<LibraryEntry | null>(null);
  // Esc closes the fork editor first, then the gallery.
  useEscapeKey(() => { if (forkOf) setForkOf(null); else if (open) setOpen(false); }, open || !!forkOf);

  // Load curated + public user blocks whenever the modal is open and filters change.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    loadLibrary({ category, query: query.trim() || undefined })
      .then((e) => { if (alive) setEntries(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, category, query]);

  const results = useMemo(() => entries, [entries]);
  const selected = useMemo(() => entries.find((e) => e.id === selectedId), [entries, selectedId]);

  // Killed/disabled library → no UI access (acceptance criterion). After all hooks (Rules of Hooks).
  if (!gate.library.enabled) return null;

  const add = (entry: LibraryEntry) => {
    let block: import('../../types').Block;
    try {
      block = buildEntryBlock(entry, `cb_${entry.id.replace(/[^a-z0-9]+/gi, '_')}_${blocks.length + 1}`);
    } catch (e) {
      // TSL-only install contract — a pre-TSL entry without a tslAst would be a quarantined no-op; refuse loudly.
      window.alert(String((e as Error)?.message ?? e));
      return;
    }
    patchBlocks([...blocks, block]); setOpen(false); setSelectedId(null);
  };

  const report = async (entry: LibraryEntry) => {
    const reason = (window.prompt('Report this block — reason (unsafe / incorrect / misleading / plagiarism / other):', 'unsafe') || '').trim().toLowerCase();
    if (!['unsafe', 'incorrect', 'misleading', 'plagiarism', 'other'].includes(reason)) return;
    const r = await reportBlock(entry.id, reason as 'unsafe', window.prompt('Optional detail:') || undefined, entry.version);
    if (r.ok) setReported((m) => ({ ...m, [entry.id]: true }));
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
        <Library className="w-4 h-4" /> Block Library
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800"><Library className="w-4 h-4" /> Custom Block Library <span className="text-xs font-normal text-gray-400">curated + community · beta</span></div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex items-center gap-2 px-4 py-2 border-b">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search blocks…" className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md" />
              </div>
              <button onClick={() => setCategory(undefined)} className={`px-2 py-1 text-xs rounded ${!category ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}>All</button>
              {CATEGORIES.map((c) => (
                <button key={c.key} onClick={() => setCategory(c.key)} className={`px-2 py-1 text-xs rounded ${category === c.key ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600'}`}>{c.label}</button>
              ))}
            </div>

            <div className="flex flex-1 min-h-0">
              <div className="w-1/2 overflow-y-auto border-r">
                {loading && <div className="p-4 text-sm text-gray-400">Loading…</div>}
                {!loading && results.length === 0 && <div className="p-4 text-sm text-gray-400">No blocks match.</div>}
                {results.map((b) => (
                  <button key={b.id} onClick={() => setSelectedId(b.id)} className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 ${selectedId === b.id ? 'bg-gray-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-800 flex items-center gap-1.5">{b.name}
                        {b.source === 'builtin'
                          ? <span title="Curated built-in" className="inline-flex items-center gap-0.5 text-[9px] text-blue-600"><BadgeCheck className="w-3 h-3" />curated</span>
                          : <span title="Community block" className="inline-flex items-center gap-0.5 text-[9px] text-gray-400"><User className="w-3 h-3" />community</span>}
                      </span>
                      <span className="text-[10px] uppercase tracking-wide text-gray-400">{b.category}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{b.description}</div>
                  </button>
                ))}
              </div>

              <div className="w-1/2 overflow-y-auto p-4">
                {!selected && <div className="text-sm text-gray-400">Select a block to see details.</div>}
                {selected && (
                  <div className="space-y-3">
                    <div>
                      <div className="text-base font-semibold text-gray-900">{selected.name}</div>
                      <div className="text-xs text-gray-500">
                        {selected.category} · {selected.blockType} · v{selected.version}
                        {selected.source === 'user' && <> · by {selected.author?.slice(0, 8) ?? 'community'}</>}
                        {typeof selected.downloads === 'number' && <> · {selected.downloads} uses</>}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700">{selected.description}</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <TierBadge label="public" on={selected.tier.publicVisible} />
                      <TierBadge label="AI-safe" on={selected.tier.aiSafe} />
                      <TierBadge label="optimizer-safe" on={selected.tier.optimizerSafe} />
                      {selected.source === 'builtin'
                        ? <span className="inline-flex items-center gap-1 text-[10px] text-blue-600"><BadgeCheck className="w-3 h-3" /> curated built-in</span>
                        : <span className="text-[10px] text-gray-400">community-published · gate-verified</span>}
                    </div>
                    {selected.riskNotes && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"><b>Risk notes:</b> {selected.riskNotes}</div>}
                    {selected.example && <div className="text-xs text-gray-600"><b>Example:</b> {selected.example}</div>}
                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-1">Parameters</div>
                      <table className="w-full text-xs">
                        <tbody>
                          {Object.entries(selected.paramSchema).map(([k, s]) => (
                            <tr key={k} className="border-b last:border-0">
                              <td className="py-1 pr-2 font-mono text-gray-700">{s.label ?? k}</td>
                              <td className="py-1 text-gray-500">{s.kind}{s.kind === 'enum' ? ` (${(s.values ?? []).join(', ')})` : (typeof s.min === 'number' ? ` [${s.min}–${s.max}]` : '')} · default {String(s.default)}{s.optimizable ? '' : ' · fixed'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <details>
                      <summary className="text-xs text-gray-600 cursor-pointer">View code</summary>
                      <pre className="text-[10px] bg-gray-900 text-gray-100 rounded p-2 overflow-x-auto max-h-48 mt-1">{selected.code}</pre>
                    </details>
                    <div className="flex items-center gap-2">
                      <button onClick={() => add(selected)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
                        <Plus className="w-4 h-4" /> Add to strategy
                      </button>
                      {selected.tslAst && (
                        <button onClick={() => setForkOf(selected)} title="Open a private, editable copy in the TSL Block Studio (records derivation provenance)" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                          <GitFork className="w-3.5 h-3.5" /> Fork
                        </button>
                      )}
                      {selected.source === 'user' && (
                        <button onClick={() => report(selected)} disabled={reported[selected.id]} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                          <Flag className="w-3.5 h-3.5" /> {reported[selected.id] ? 'Reported' : 'Report'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {forkOf?.tslAst && (
        <TslBlockStudio
          initialText={printBlock(forkOf.tslAst)}
          fork={{ id: forkOf.id, version: forkOf.version }}
          onClose={() => setForkOf(null)}
        />
      )}
    </>
  );
};

export default CustomBlockLibraryButton;
